import { EVVRecord, Note } from '@/types/contracts'
import api from './api'

export interface TherapistContractsParams {
  page: number
  limit: number
  status?: string
  startDate?: string
  endDate?: string
  search?: string
  specialization?: string
  insuranceId?: string
  coverageType?: string
  paymentMethod?: string
}

export interface TherapistContractApiModel {
  _id: string
  contract_ids?: string
  patient_id?: {
    _id?: string
    firstName?: string
    lastName?: string
    dob?: string
  }
  therapist_id?: {
    _id?: string
    firstName?: string
    lastName?: string
  }
  service_id?: {
    _id?: string
    name?: string
  } | null
  status?: string
  session_no?: number
  total_session?: number
  completed_session?: number
  start_date?: string
  end_date?: string
  hasPatientSigned?: boolean
  hasTherapistSigned?: boolean
  isCancelled?: boolean
  is_active?: boolean
  [key: string]: any
}

export interface TherapistContractsResponse {
  contracts: TherapistContractApiModel[]
  totalRecords: number
  currentPage: number
  totalPages: number
  totalActive: number
  totalCompleted: number
  totalCancelled: number
  totalPending: number
}

export interface ContractSessionApiModel {
  _id: string
  contract_id?: {
    _id?: string
    contract_ids?: string
    patient_id?: string
    therapist_id?: string
    session_no?: number
    member_id?: string
    booking_for?: string
    payment_method_type?: string
    status?: string
    total_session?: number
    total_amount?: number
    completed_session?: number
    start_date?: string
    end_date?: string
    next_date?: string
    envelope_id?: string
    hasPatientSigned?: boolean
    hasTherapistSigned?: boolean
    Contracts?: string[]
  }
  contract_ids?: string
  session_ids?: string
  patient_id?: {
    _id?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    dob?: string
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
    profilePhotoUrl?: string
    completedSessions?: number
    pendingSessions?: number
    signedContracts?: number
  }
  therapist_id?: {
    _id?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    providerNpi?: string
    billingNpi?: string
    profilePhotoUrl?: string
  }
  service_id?: {
    _id?: string
    name?: string
    modality?: string[]
    session_length?: number
    private_pay_rate?: number
  } | null
  session_date?: string
  session_slot?: string
  duration_mins?: number
  session_amount?: number
  payer_active?: string
  claimed_amount?: number
  approved_amount?: number
  co_pay?: number
  status?: string
  is_deleted?: boolean
  createdAt?: string
  updatedAt?: string
  evvs?: any
  notes?: any[]
}

export interface ContractSessionsResponse {
  sessions: ContractSessionApiModel[]
  totalRecords: number
  currentPage: number
  totalPages: number
}

export const getTherapistContracts = async (
  params: TherapistContractsParams
): Promise<TherapistContractsResponse> => {
  const response = await api.get<TherapistContractsResponse>(
    '/contracts/get-contracts-therapist',
    {
      params,
    }
  )

  return response.data
}

export const getContractSessions = async (
  contractId: string
): Promise<ContractSessionsResponse> => {
  const response = await api.get<ContractSessionsResponse>(
    `/contracts/get-session/${contractId}`
  )

  return response.data
}

// Contract Timeline Types
export interface ContractTimelineItem {
  _id: string
  contract_id?: string | null
  title: string
  timelineType: string
  session_id?: string | null
  therapist_id?: {
    _id?: string
    firstName?: string
    lastName?: string
    specialization?: string[]
    yearsOfExperience?: number
    profilePhotoUrl?: string
  } | null
  patient_id?: {
    _id?: string
    firstName?: string
    lastName?: string
  } | null
  isDeleted?: boolean
  createdAt: string
  updatedAt: string
  __v?: number
}

export interface ContractTimelineResponse {
  data?: ContractTimelineItem[]
  [key: string]: any
}

// Contract Document Types
export interface ContractDocument {
  _id: string
  contract_id: string
  title: string
  fileUrl: string
  fileKey: string
  patientId: string
  therapistId: string
  isDeleted?: boolean
  createdAt: string
  updatedAt: string
  __v?: number
}

export interface ContractDocumentsResponse {
  totalRecords: number
  totalPages: number
  currentPage: number
  limit: number
  data: ContractDocument[]
}

// API Functions
export const getContractTimeline = async (
  contractId: string
): Promise<ContractTimelineItem[]> => {
  const response = await api.get<ContractTimelineResponse>(
    `/contracts/${contractId}/timeline`
  )
  // If response.data is an array, return it directly
  if (Array.isArray(response.data)) {
    return response.data
  }
  // Otherwise, extract the data property if it exists
  return response.data?.data || []
}

export const getContractDocuments = async (
  contractId: string,
  page: number = 1,
  limit: number = 10
): Promise<ContractDocumentsResponse> => {
  const response = await api.get<ContractDocumentsResponse>(
    `/contracts/${contractId}/documents`,
    {
      params: { page, limit },
    }
  )
  return response.data
}

export const downloadContractDocument = async (
  documentId: string
): Promise<string> => {
  const response = await api.get<{ signedUrl?: string }>(
    `/contracts/documents/${documentId}/download`
  )
  return response.data?.signedUrl || ''
}

export const cancelTherapistContract = async (
  contractId: string,
  cancelDate: string
) => {
  const response = await api.patch(
    `contracts/cancel-contract-therapist/${contractId}`,
    {
      cancel_date: cancelDate,
    }
  )
  return response.data
}
